import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTablePreferences } from "./utils/useTablePreferences";
import DropdownColumnFilterStudent from "./DropdownColumnFilterStudent";
import { fetchBatchOptions } from "../pages/services/filterOptionsService";
import "../styles/ListPage.css";
import "../styles/StudentList.css";

// Column key -> the query param the backend expects.
const FILTER_KEY_MAP = {
  name: "studentName",
  batch: "batchTitle",
  city: "city",
  course: "courseTitle",
  // Not "status": students already have a status field (Enrolled/Completed/...)
  // and this is the separate active/inactive switch.
  activeStatus: "activeStatus",
};

// Deactivated students are hidden everywhere by default; this is the only
// screen that can bring them back into view.
const ACTIVE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "all", label: "All" },
];

const FILTER_LABELS = { name: "Name", batch: "Batch", city: "City", course: "Course" };

// "8 Sep 2026, 3:35 pm" — short enough to sit above the activity detail
// without wrapping at the usual column width.
const formatActivityDate = (value) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
};

// Batches and courses are both arrays now, so a student can legitimately show
// several of each.
const listOrDash = (values) => {
  const clean = (values || []).filter(Boolean);
  return clean.length ? clean : null;
};

const StudentList = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage, sortConfig, setSortConfig } = useTablePreferences("table_prefs_students");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ name: "", batch: "", city: "", course: "", activeStatus: "active" });
  const [loading, setLoading] = useState(false);

  // Import flow state
  const [showImportModal, setShowImportModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);         // { message: string }
  const [importSuccess, setImportSuccess] = useState(null);     // { students: string[] }
  const API_URL = process.env.REACT_APP_API_URL;
  const filterChangedRef = useRef(false);
  const fileInputRef = useRef(null);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    const queryParams = new URLSearchParams();
    queryParams.append("page", page + 1);
    queryParams.append("pageSize", rowsPerPage);
    if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());

    Object.entries(filters).forEach(([key, value]) => {
      if ((value || "").toString().trim()) queryParams.append(FILTER_KEY_MAP[key], value.trim());
    });
    if (sortConfig.column && sortConfig.direction) {
      queryParams.append("sortColumn", sortConfig.column);
      queryParams.append("sortDirection", sortConfig.direction);
    }

    try {
      const res = await fetch(`${API_URL}/api/student?${queryParams.toString()}`);
      const response = await res.json();
      setStudents(response.data || []);
      setTotalCount(response.pagination?.totalItems || 0);
    } catch (err) { console.error("Fetch Error:", err); }
    setLoading(false);
  }, [page, rowsPerPage, searchTerm, filters, sortConfig]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  useEffect(() => {
    if (filterChangedRef.current) { setPage(0); filterChangedRef.current = false; }
  }, [filters, searchTerm]);

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const opts = await fetchBatchOptions();
        setBatches(opts.map(o => ({ _id: o.value, batchTitle: o.label })));
      } catch (err) { console.error("Error fetching batches:", err); }
    };
    fetchBatches();
  }, []);

  const handleFilterChange = (key, value) => {
    filterChangedRef.current = true;
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onSort = (column) => {
    const columnMap = {
      name: "studentName", batch: "batchTitle", city: "city",
      course: "courseTitle", lastActivity: "lastActivityAt",
    };
    const backendColumn = columnMap[column];
    setPage(0);
    setSortConfig((prev) => {
      if (prev.column === backendColumn) {
        if (prev.direction === "asc") return { column: backendColumn, direction: "desc" };
        if (prev.direction === "desc") return { column: null, direction: null };
      }
      return { column: backendColumn, direction: "asc" };
    });
  };

  const thClass = (col) => sortConfig.column === col
    ? (sortConfig.direction === "asc" ? "lp-sort-asc" : "lp-sort-desc")
    : "";

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleRowClick = (id) => navigate(`/students/${id}`);
  const handleAddStudent = () => navigate("/students/add");

  const handleExportCSV = async () => {
    const queryParams = new URLSearchParams();
    if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());
    Object.entries(filters).forEach(([key, value]) => {
      const v = (value ?? "").toString().trim();
      if (v) queryParams.append(FILTER_KEY_MAP[key], v);
    });
    try {
      const response = await fetch(`${API_URL}/api/students-export?${queryParams.toString()}`, { headers: { Accept: "text/csv" } });
      if (!response.ok) throw new Error("Failed to export");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      link.href = url; link.download = `students-${timestamp}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { console.error("CSV download error:", error); }
  };

  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.href = "/sample-student.csv";
    link.download = "sample-student.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const processImportFile = async (file) => {
    if (!file) return;
    setShowImportModal(false);
    setImportLoading(true);
    setImportError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch("/api/students-import", { method: "POST", body: formData });
      const response = await res.json();

      if (response.success) {
        fetchStudents();
        setImportSuccess({ students: response.students || [] });
      } else {
        setImportError({ message: response.message || "Import failed. Please check your CSV and try again." });
      }
    } catch (err) {
      console.error("Import Error:", err);
      setImportError({ message: "A network error occurred. Please try again." });
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="lp-page">
      {/* ── Import loading overlay ── */}
      {importLoading && (
        <div className="lp-overlay">
          <div className="lp-import-dialog">
            <div className="lp-import-spinner" />
            <h3>Importing Students…</h3>
            <p>Validating and saving student data. Please wait.</p>
          </div>
        </div>
      )}

      {/* ── Error dialog ── */}
      {importError && (
        <div className="lp-overlay">
          <div className="lp-modal sl-error-modal">
            <div className="sl-modal-icon sl-modal-icon--error">✕</div>
            <h3>Import Failed</h3>
            <div className="sl-error-body">
              {importError.message.split("\n").map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            <p className="sl-error-hint">Fix the issue(s) above and try uploading again. You can download the sample CSV for reference.</p>
            <div className="sl-modal-actions">
              <button className="lp-btn-secondary" onClick={handleDownloadSample}>Download Sample</button>
              <button className="lp-modal-btn" onClick={() => { setImportError(null); setShowImportModal(true); }}>Try Again</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Success summary dialog ── */}
      {importSuccess && (
        <div className="lp-overlay">
          <div className="lp-modal sl-success-modal">
            <div className="sl-modal-icon sl-modal-icon--success">✓</div>
            <h3>Import Successful</h3>
            <p>{importSuccess.students.length} student(s) added to the database.</p>
            {importSuccess.students.length > 0 && (
              <div className="sl-success-table-wrap">
                <table className="sl-success-table">
                  <thead>
                    <tr><th>#</th><th>Student Name</th></tr>
                  </thead>
                  <tbody>
                    {importSuccess.students.map((name, i) => (
                      <tr key={i}><td>{i + 1}</td><td>{name}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button className="lp-modal-btn" style={{ marginTop: "1.2rem" }} onClick={() => setImportSuccess(null)}>Close</button>
          </div>
        </div>
      )}

      {/* ── Import file-picker modal ── */}
      {showImportModal && (
        <div className="lp-overlay" onClick={() => setShowImportModal(false)}>
          <div className="lp-drop-modal" onClick={(e) => e.stopPropagation()}>
            <div
              className={`lp-drop-zone${dragOver ? " lp-drag-active" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) processImportFile(file);
              }}
            >
              <div className="sl-upload-icon">&#128196;</div>
              <h3>Import Students</h3>
              <p>Drag &amp; drop a CSV file here, or click <strong>Browse Files</strong> to select one.</p>
              <div className="lp-drop-actions">
                <button className="lp-btn-primary" onClick={() => fileInputRef.current?.click()}>Browse Files</button>
                <button className="lp-btn-secondary" onClick={handleDownloadSample}>Download Sample</button>
              </div>
              <p className="sl-drop-note">
                Columns marked <strong>(Required)</strong> in the sample CSV must be filled in.<br />
                Columns marked <strong>(Optional)</strong> can be left blank.
              </p>
              <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = null;
                  if (file) processImportFile(file);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">All Students</h2>
          <span className="lp-count">{totalCount} records</span>
        </div>
        <div className="lp-header-actions">
          <button className="lp-btn-primary" onClick={handleAddStudent}>+ Add Student</button>
          <button className="lp-btn-secondary" onClick={handleExportCSV}>Export CSV</button>
          <button className="lp-btn-secondary" onClick={() => setShowImportModal(true)}>Import Students</button>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input
            type="text"
            placeholder="Search students…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="lp-search"
          />
        </div>
        <div className="lp-filters">
          <div className="sl-status-filter">
            <label htmlFor="sl-activeStatus">Status</label>
            <select
              id="sl-activeStatus"
              value={filters.activeStatus}
              onChange={(e) => handleFilterChange("activeStatus", e.target.value)}
            >
              {ACTIVE_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {["name","batch","city","course"].map((key) => (
            <DropdownColumnFilterStudent
              key={key}
              column={{
                filterValue: filters[key],
                setFilter: (value) => handleFilterChange(key, value),
                // Batch and course are multi-valued per student, so each of a
                // student's values becomes its own selectable row.
                preFilteredRows: students.flatMap((row) => {
                  const values =
                    key === "name"   ? [row.studentName] :
                    key === "city"   ? [row.city] :
                    key === "batch"  ? (row.batchTitles || []) :
                                       (row.courseTitles || []);
                  return values
                    .filter((v) => v !== "" && v !== null && v !== undefined)
                    .map((v) => ({ values: { [key]: String(v) } }));
                }),
                id: key,
                label: FILTER_LABELS[key],
              }}
            />
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="lp-table-card">
        {loading ? (
          <div className="lp-state"><div className="lp-spinner" /><p>Loading students…</p></div>
        ) : students.length === 0 ? (
          <div className="lp-state"><p>No students found.</p></div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                <th className={thClass("studentName")} onClick={() => onSort("name")}>Student Name</th>
                <th className={thClass("batchTitle")} onClick={() => onSort("batch")}>Batches</th>
                <th className={thClass("city")} onClick={() => onSort("city")}>City</th>
                <th className={thClass("courseTitle")} onClick={() => onSort("course")}>Course</th>
                <th className={thClass("lastActivityAt")} onClick={() => onSort("lastActivity")}>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => {
                const studentBatches = listOrDash(
                  student.batchTitles?.length
                    ? student.batchTitles
                    : [batches.find((b) => String(b._id) === String(student.batchId))?.batchTitle]
                );
                const studentCourses = listOrDash(student.courseTitles);
                const activity = student.lastActivity;

                return (
                  <tr key={student._id} onClick={() => handleRowClick(student._id)}>
                    <td>{student.studentName}</td>
                    <td>
                      {studentBatches ? (
                        <div className="sl-tag-list">
                          {studentBatches.map((title, i) => (
                            <span className="sl-tag" key={i}>{title}</span>
                          ))}
                        </div>
                      ) : "—"}
                    </td>
                    <td>{student.city || "—"}</td>
                    <td>
                      {studentCourses ? (
                        <div className="sl-tag-list">
                          {studentCourses.map((title, i) => (
                            <span className="sl-tag sl-tag--course" key={i}>{title}</span>
                          ))}
                        </div>
                      ) : "—"}
                    </td>
                    <td>
                      {activity ? (
                        <div className="sl-activity">
                          <span className="sl-activity-time">{formatActivityDate(activity.timestamp)}</span>
                          {activity.details && (
                            <span className="sl-activity-detail">{activity.details}</span>
                          )}
                        </div>
                      ) : (
                        <span className="sl-activity-none">No activity yet</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      {!loading && students.length > 0 && (
        <div className="lp-pagination">
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0}>← Previous</button>
          <span className="lp-page-info">Page {page + 1} of {totalPages}</span>
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))} disabled={page >= totalPages - 1}>Next →</button>
          <div className="lp-page-size">
            <label htmlFor="sl-pageSize">Rows:</label>
            <select id="sl-pageSize" value={rowsPerPage} onChange={(e) => { setPage(0); setRowsPerPage(Number(e.target.value)); }}>
              {[10,20,30,50,75,100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;
